import Descriptions from 'antd/es/descriptions';
import Form from 'antd/es/form';
import Select from 'antd/es/select';
import Typography from 'antd/es/typography';
import styled from 'styled-components';

import { type TaskTemplate } from '@mai/types';

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FormSection = styled.div`
  /* Keep form items at the top, not scrollable */
  flex-shrink: 0;
`;

const DetailsSection = styled.div`
  /* Allow this section to scroll while keeping the select fixed */
  flex-grow: 1;
  overflow-y: auto;
  padding-right: ${({ theme }) => `${theme.token.paddingSM}px`};
`;

const ScrollableDescriptions = styled(Descriptions)`
  .ant-descriptions-view {
    max-height: 100%;
    overflow-y: visible; /* Let the parent control scrolling */
  }
`;

const FieldList = styled.ul`
  padding-left: 20px;
  margin-bottom: 0;
`;

type TemplateSelectionStepProps = {
  templates: TaskTemplate[];
  selectedTemplate: TaskTemplate | null;
  onTemplateSelect: (templateId: string | null) => void;
  allowNoTemplate?: boolean;
};

const TemplateSelectionStep = ({
  templates,
  selectedTemplate,
  onTemplateSelect,
  allowNoTemplate = false,
}: TemplateSelectionStepProps) => {
  return (
    <StepContainer>
      <FormSection>
        <Form.Item label="Choose a template">
          <Select
            placeholder="Select a template"
            value={
              selectedTemplate?.taskTemplateId ||
              (allowNoTemplate && !selectedTemplate ? 'no-template' : undefined)
            }
            onChange={(value) =>
              onTemplateSelect(value === 'no-template' ? null : value)
            }
            style={{ width: '100%' }}
          >
            {allowNoTemplate && (
              <Select.Option key="no-template" value="no-template">
                <Typography.Text type="secondary">No Template</Typography.Text>
              </Select.Option>
            )}
            {templates.map((template) => (
              <Select.Option
                key={template.taskTemplateId}
                value={template.taskTemplateId}
              >
                {template.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </FormSection>

      <DetailsSection>
        {selectedTemplate ? (
          <ScrollableDescriptions column={1} bordered layout="vertical">
            <Descriptions.Item label="Description">
              {selectedTemplate.description}
            </Descriptions.Item>
            <Descriptions.Item label="Fields">
              <FieldList>
                {selectedTemplate.fields.map((field) => (
                  <li key={field.name}>
                    <strong>{field.title}</strong>
                    {field.required ? ' (Required)' : ''}
                    {field.description ? `: ${field.description}` : ''}
                  </li>
                ))}
              </FieldList>
            </Descriptions.Item>
          </ScrollableDescriptions>
        ) : allowNoTemplate && !selectedTemplate ? (
          <ScrollableDescriptions column={1} bordered layout="vertical">
            <Descriptions.Item label="Description">
              Create a custom task with your own title and description. This
              option gives you full flexibility to define any type of task
              without the constraints of a predefined template.
            </Descriptions.Item>
            <Descriptions.Item label="Fields">
              <FieldList>
                <li>
                  <strong>Title</strong> (Required): A clear, concise name for
                  your task
                </li>
                <li>
                  <strong>Description</strong> (Required): Detailed explanation
                  of what needs to be done
                </li>
              </FieldList>
            </Descriptions.Item>
          </ScrollableDescriptions>
        ) : null}
      </DetailsSection>
    </StepContainer>
  );
};

export default TemplateSelectionStep;
